import React, {useState, useEffect, useImperativeHandle, forwardRef} from "react";
import PropTypes from "prop-types";
import {GSPSToolbarSelectField} from "Components/Common/GSPSToolbarSelectField";
import {insertNotification} from "Utils/NotificationsUtils";
import {AuditItemType, AuditItemTypeTitle} from "../../Models/Audit/AuditConstants";
import {capitalize} from "lodash";

export const SEVERITY_LABEL = `${capitalize(AuditItemType.PIN)} Severity`;
export const PHASE_LABEL = `${capitalize(AuditItemType.PIN)} Phase`;

export const getPhaseName = (phase) => {
    if (phase === null) {
        return "N/A";
    }

    return `Phase ${phase}`;
};

export const SVPMapFilter = forwardRef(({
    annotationMarkers,
    annotationLines,
    annotationAreas,
    auditIssues,
    auditLines,
    auditAreas,
    auditsFilter,
    setAuditsFilter,
}, ref) => {
    const [auditFilterOptionsList, setAuditFilterOptionsList] = useState([]);

    // Expose functions for managing filters to the parent via the ref
    useImperativeHandle(ref, () => ({
        callReplaceTypeItemFilter(prevTypeName, newTypeName, annotationType) {
            replaceTypeItemFilter(prevTypeName, newTypeName, annotationType);
        },
        callAddTypeToAuditsFilterIfNotExists(annotationType, itemTypeName) {
            addTypeToAuditsFilterIfNotExists(annotationType, itemTypeName);
        },
        callDeleteTypeFromAuditsFilterIfExists(annotationType, itemTypeName) {
            deleteTypeFromAuditsFilterIfExists(annotationType, itemTypeName);
        },
        callResetAuditTypesOptions(prevTypeName, newTypeName, annotationType) {
            resetAuditTypesOptions(prevTypeName, newTypeName, annotationType);
        },
    }));

    // Populate the filter options list based on the provided audit data
    useEffect(() => {
        if (auditIssues) {
            setAuditFilterOptionsList((prev) => {
                const isTypesAdded = prev.some((option) => option.label === AuditItemTypeTitle.PINS);
                const isSeveritiesAdded = prev.some((option) => option.label === SEVERITY_LABEL);
                const isPhasesAdded = prev.some((option) => option.label === PHASE_LABEL);

                const newAuditsFilter = [
                    ...prev,
                    ...(!isTypesAdded ?
                        [{checked: true,
                            label: AuditItemTypeTitle.PINS,
                            children: extractAuditTypes(auditIssues)}] : []),
                    ...(!isSeveritiesAdded ?
                        [{checked: true,
                            label: SEVERITY_LABEL,
                            children: extractSeverities(auditIssues)}] : []),
                    ...(!isPhasesAdded ?
                        [{checked: true,
                            label: PHASE_LABEL,
                            children: extractPhases(auditIssues)}] : []),
                ];
                return newAuditsFilter;
            });
        }
        if (auditLines) {
            setAuditFilterOptionsList((prev) => {
                const isAdded = prev.some((option) => option.label === AuditItemTypeTitle.LINES);
                return isAdded ?
                    prev : [...prev, {checked: true, label: AuditItemTypeTitle.LINES,
                        children: extractAuditTypes(auditLines)}];
            });
        }
        if (auditAreas) {
            setAuditFilterOptionsList((prev) => {
                const isAdded = prev.some((option) => option.label === AuditItemTypeTitle.AREAS);
                return isAdded ?
                    prev : [...prev, {checked: true,
                        label: AuditItemTypeTitle.AREAS,
                        children: extractAuditTypes(auditAreas)}];
            });
        }
    }, [auditIssues, auditLines, auditAreas]);

    // Update severity filter options when annotationMarkers change
    useEffect(() => {
        if (!annotationMarkers || annotationMarkers.length === 0) {
            return;
        }

        setAuditFilterOptionsList((prev) => {
            const severityOptionIndex = prev.findIndex((option) => option.label === SEVERITY_LABEL);
            const phaseOptionIndex = prev.findIndex((option) => option.label === PHASE_LABEL);

            const currentMarkerSeverityNames = [
                ...new Set(annotationMarkers.map((marker) => marker.severity_object.name?.trim()).filter(Boolean)),
            ];
            const currentMarkerPhaseNames = [
                ...new Set(annotationMarkers.map((marker) => getPhaseName(marker.item_phase)).filter(Boolean)),
            ];

            if (severityOptionIndex !== -1) {
                const updatedOptions = [...prev];

                const existingSeverityGroup = updatedOptions[severityOptionIndex];
                const newSeverityChildren = currentMarkerSeverityNames.map((name) => {
                    const existingChild = existingSeverityGroup.children.find((child) => child.label === name);
                    return {
                        label: name,
                        // Preserve checked state, or default to true for new severities
                        checked: existingChild ? existingChild.checked : true,
                    };
                });

                const existingPhaseGroup = updatedOptions[phaseOptionIndex];
                const newPhaseChildren = currentMarkerPhaseNames.map((name) => {
                    const existingChild = existingPhaseGroup .children.find((child) => child.label === name);
                    return {
                        label: name,
                        // Preserve checked state, or default to true for new severities
                        checked: existingChild ? existingChild.checked : true,
                    };
                });

                updatedOptions[severityOptionIndex] = {
                    ...existingSeverityGroup,
                    children: newSeverityChildren,
                };
                updatedOptions[phaseOptionIndex] = {
                    ...existingPhaseGroup,
                    children: newPhaseChildren,
                };
                return updatedOptions;
                // Severity group doesn't exist, but there are severities
            } else if (currentMarkerSeverityNames.length > 0) {
                return [
                    ...prev,
                    {
                        checked: true,
                        label: SEVERITY_LABEL,
                        children: currentMarkerSeverityNames.map((name) => ({label: name, checked: true})),
                    },
                    {
                        checked: true,
                        label: PHASE_LABEL,
                        children: currentMarkerPhaseNames.map((name) => ({label: name, checked: true})),
                    },
                ];
            }
            return prev;
        });
    }, [annotationMarkers]);

    // Sync the audit filters with the selected options
    useEffect(() => {
        const updatedAuditsFilter = {
            pins: getCheckedLabels(auditFilterOptionsList, AuditItemTypeTitle.PINS),
            severity: getCheckedLabels(auditFilterOptionsList, SEVERITY_LABEL),
            phases: getCheckedLabels(auditFilterOptionsList, PHASE_LABEL),
            lines: getCheckedLabels(auditFilterOptionsList, AuditItemTypeTitle.LINES),
            areas: getCheckedLabels(auditFilterOptionsList, AuditItemTypeTitle.AREAS),
        };

        setAuditsFilter(updatedAuditsFilter);
    }, [auditFilterOptionsList]);

    // Utility to get checked labels for a specific filter category
    const getCheckedLabels = (optionsList, label) => {
        const option = optionsList.find((opt) => opt.label.toLowerCase() === label.toLowerCase());
        return option ? option.children.filter((child) => child.checked).map((child) => child.label) : [];
    };

    // Update the audit options list with new data or updates
    const updateAuditFilterOptionsList = (auditFilterOptionsList, value) => {
        const updatedList = auditFilterOptionsList.map((option) => {
            const key = option.label.toLowerCase();
            const correspondingValue = value[key] ?? [];
            if (correspondingValue) {
                option.checked = correspondingValue?.length > 0;
                option.children = option.children.map((child) => ({
                    ...child,
                    checked: correspondingValue.includes(child.label),
                }));
            }
            return option;
        });
        return updatedList;
    };

    // Extract audit types from the provided items
    const extractAuditTypes = (items) => {
        const auditTypes = items
            .map((item) => ({label: item.audit_type_item.name?.trim(), checked: true}))
            .filter((obj, index, self) =>
                self.findIndex((element) => element.label === obj.label) === index);
        return auditTypes;
    };

    // Extract audit severities from the provided items
    const extractSeverities = (items) => {
        const severities = items
            .map((item) => ({label: item.severity_object.name?.trim(), checked: true}))
            .filter((obj, index, self) =>
                self.findIndex((element) => element.label === obj.label) === index);
        return severities;
    };

    const extractPhases = (items) => {
        const phases = items
            .map((item) => ({label: getPhaseName(item.item_phase), checked: true}))
            .filter((obj, index, self) =>
                self.findIndex((element) => element.label === obj.label) === index);
        return phases;
    };

    // Add a type to the audits filter if it doesn't exist
    const addTypeToAuditsFilterIfNotExists = (annotationType, typeName) => {
        const auditTypes = getAnnotationAuditTypes(annotationType);
        if (auditTypes && !auditTypes.children.some((child) => child.label === typeName?.trim())) {
            setAuditFilterOptionsList((prevState) => {
                const updatedChildren = [...auditTypes.children, {label: typeName?.trim(), checked: true}];
                const updatedAuditFilterOptionsList = prevState.map((option) => {
                    if (option.label === auditTypes.label) {
                        return {...option, checked: true, children: updatedChildren};
                    }
                    return option;
                });
                return updatedAuditFilterOptionsList;
            });
        }
    };

    // Remove a type from the audits filter if it exists
    const deleteTypeFromAuditsFilterIfExists = (annotationType, typeName) => {
        const auditTypes = getAnnotationAuditTypes(annotationType);
        const annotationMap = {
            Items: annotationMarkers,
            Severity: annotationMarkers,
            Phases: annotationMarkers,
            Lines: annotationLines,
            Areas: annotationAreas,
        };
        const annotationArray = annotationMap[auditTypes.label];

        const auditTypeOccurs = annotationArray?.filter(
            (item) => {
                if (auditTypes?.label === SEVERITY_LABEL) {
                    return item.severity_object.name?.trim() === typeName.trim();
                }
                if (auditTypes?.label === PHASE_LABEL) {
                    return getPhaseName(item.item_phase) === typeName.trim();
                }
                return item.audit_type_item.name?.trim() === typeName.trim();
            },
        )?.length;
        setAuditFilterOptionsList((prevState) => {
            return prevState.map((option) => {
                if (option.label !== auditTypes.label) return option;

                if (auditTypeOccurs === 1) {
                    const updatedChildren = option.children.filter(
                        (child) => child.label !== typeName,
                    );
                    return {...option, checked: true, children: updatedChildren};
                }

                return option;
            });
        });
    };

    // Replace a type in the audits filter
    const replaceTypeItemFilter = (prevTypeName, newTypeName, annotationType) => {
        addTypeToAuditsFilterIfNotExists(annotationType, newTypeName);
        deleteTypeFromAuditsFilterIfExists(annotationType, prevTypeName?.trim());
    };

    // Handle filter selection updates
    const handleFilterSelection = (value) => {
        try {
            const updatedAuditFilterOptionsList = updateAuditFilterOptionsList(auditFilterOptionsList, value);
            setAuditFilterOptionsList(updatedAuditFilterOptionsList);
            setAuditsFilter(value);
        } catch (e) {
            insertNotification("Error", `can't filter. Reason: ${e.message}`, "error");
        }
    };

    // Reset the options for a specific annotation type
    const resetAuditTypesOptions = (annotationType) => {
        setAuditFilterOptionsList((currentAuditFilterOptionsList) => currentAuditFilterOptionsList.map((option) => {
            if (option.label === annotationType) {
                return {
                    ...option,
                    children: [],
                };
            }
            return {
                ...option,
                children: [...option.children],
            };
        }));
    };

    // Get options for a specific annotation type
    const getAnnotationAuditTypes = (annotationType) => {
        return auditFilterOptionsList.find((option) => option.label === annotationType);
    };

    return (
        <GSPSToolbarSelectField
            inputID="filter_audits"
            selectableOptions={auditFilterOptionsList}
            onChangeCB={(value) => {
                handleFilterSelection(value);
            }}
            renderedElementType={"filter"}
        />
    );
});

SVPMapFilter.displayName = "SVPMapFilter";

SVPMapFilter.propTypes = {
    annotationMarkers: PropTypes.array,
    annotationLines: PropTypes.array,
    annotationAreas: PropTypes.array,
    auditIssues: PropTypes.array,
    auditLines: PropTypes.array,
    auditAreas: PropTypes.array,
    auditsFilter: PropTypes.object.isRequired,
    setAuditsFilter: PropTypes.func.isRequired,
};
